import { BtnPrimary, BtnSecondary, BtnWarning } from '@components/buttons';
import { IcoExclamation, IcoKeyboard, IcoMicrophone, IcoSend } from '@components/icons';
import { useState, useEffect } from 'preact/hooks';
import { AutosizeText } from '@components/autosize-text';
import { Case } from '@components/conditional';
import type { AiMessage, AiProfileSection } from 'server/types/cal-overrides';
import {
  useVoiceAssistant,
  useLocalParticipant,
  LiveKitRoom,
  useDataChannel,
  setLogLevel,
} from '@livekit/components-react';
import { useConfiguration, useCurrentUser } from 'client/lib/auth';
import { rpx } from 'client/lib/rpx-client';
import { showError } from '@components/app-error';
import { OrbWithAudio } from './orb';
import { useChatAndTranscription } from './use-chat-and-transcription';
import { LoadingIndicator } from '@components/loading-indicator';

type OnProgressData = {
  type: 'onProgress';
  section: AiProfileSection;
  value: string;
};

type OnCompleteData = {
  type: 'onComplete';
};

export type LivekitConversationData = OnProgressData | OnCompleteData;

function scrollToEnd(el: undefined | null | HTMLElement) {
  const parent = el?.closest('.js-scroll-container');
  if (!parent) {
    return;
  }
  parent.scrollTo({
    behavior: 'smooth',
    left: 0,
    top: parent.scrollHeight,
  });
}

function ConversationUI(props: {
  firstName: string;
  initialMessages: AiMessage[];
  onMessagesChange: (messages: AiMessage[]) => void;
  onData?: (data: LivekitConversationData) => void;
  setShouldConnect: (shouldConnect: boolean) => void;
}) {
  const { firstName } = props;
  const { localParticipant, lastMicrophoneError } = useLocalParticipant();
  const { state } = useVoiceAssistant();
  const { messages, send: sendTextMessage } = useChatAndTranscription(props.initialMessages);

  useDataChannel((message) => {
    if (!props.onData) {
      return;
    }
    try {
      const decoder = new TextDecoder();
      const text = decoder.decode(message.payload);
      const data = JSON.parse(text) as LivekitConversationData;
      props.onData(data);
    } catch (err) {
      console.error('Failed to parse data channel message:', err);
    }
  });

  const [isMuted, setIsMuted] = useState(false);
  const [isKeyboardConversation, setIsKeyboardConversation] = useState(false);
  const [keyboardMessage, setKeyboardMessage] = useState('');

  // If we get a microphone error, switch to keyboard conversation
  useEffect(() => {
    if (lastMicrophoneError) {
      setIsKeyboardConversation(true);
    }
  }, [lastMicrophoneError]);

  useEffect(() => {
    props.onMessagesChange(messages);
  }, [messages]);

  function getStatusText() {
    if (state === 'connecting') {
      return 'Connecting to agent...';
    }
    if (state === 'disconnected') {
      return 'Agent disconnected';
    }
    if (state === 'initializing') {
      return 'Initializing agent...';
    }
    if (state === 'listening') {
      return 'Coach Mira is listening';
    }
    if (state === 'thinking') {
      return 'Coach Mira is thinking...';
    }
    if (state === 'speaking') {
      return 'Coach Mira is speaking';
    }
    return `Status: ${state}`;
  }

  async function sendKeyboardMessage() {
    const content = keyboardMessage.trim();
    await sendTextMessage(content);
    setKeyboardMessage('');
  }

  useEffect(() => {
    const shouldMute = isKeyboardConversation || isMuted;
    localParticipant.setMicrophoneEnabled(!shouldMute);
  }, [isMuted, isKeyboardConversation, localParticipant]);

  return (
    <div class="flex grow flex-col overflow-hidden">
      <header class="px-4 pt-6 border-b">
        <h1 class="text-lg md:text-2xl font-bold">Welcome, {firstName}! Let's get to know you!</h1>
        <div class="flex items-center gap-4 p-4">
          <OrbWithAudio state={state} isMuted={isMuted} />
          {!isKeyboardConversation && (
            <BtnSecondary
              class={`p-2 rounded-full ring-0 relative ${isMuted ? 'bg-red-50 border-red-500' : ''}`}
              onClick={() => setIsMuted((prev) => !prev)}
            >
              <IcoMicrophone class={`size-5 ${isMuted ? 'text-red-500' : ''}`} />
              {isMuted && (
                <div class="absolute -inset-0.5 flex items-center justify-center pointer-events-none">
                  <div class="w-7 h-0.5 bg-red-500 rotate-45 rounded-full"></div>
                </div>
              )}
            </BtnSecondary>
          )}
          <h2 class="text-base">{getStatusText()}</h2>
        </div>
        {state === 'disconnected' && (
          <div class="bg-amber-50 border-l-4 border-amber-400 px-4 py-3 m-4 mt-0 rounded-r-lg">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-3">
                <IcoExclamation class="size-5 text-amber-600" />
                <div class="text-amber-800">
                  <p class="font-medium">Conversation paused</p>
                  <p class="text-sm">
                    I'm available to continue our conversation anytime - just resume when you're
                    ready.
                  </p>
                </div>
              </div>
              <BtnPrimary
                class="px-4 py-2 rounded-full"
                onClick={() => {
                  location.assign(`/interview?shouldStart=true`);
                }}
              >
                Resume conversation
              </BtnPrimary>
            </div>
          </div>
        )}
      </header>
      <div
        class={`js-scroll-container flex flex-col grow max-w-5xl overflow-y-auto p-8 gap-8 text-base ${state === 'disconnected' ? 'opacity-50' : ''}`}
      >
        {messages.map((x, i) => {
          const isLast = i === messages.length - 1;
          return (
            <div
              class={`flex ${x.isAssistant ? '' : 'justify-end'}`}
              key={x.id}
              ref={isLast ? scrollToEnd : undefined}
            >
              {!x.isAssistant && (
                <div class="w-2/3 p-4 rounded-2xl whitespace-break-spaces bg-blue-500 text-white items-end relative">
                  {x.content}
                </div>
              )}
              {x.isAssistant && (
                <div class="w-2/3 p-4 rounded-2xl whitespace-break-spaces bg-gray-100 items-start relative">
                  {x.content}
                </div>
              )}
            </div>
          );
        })}
        {state === 'thinking' && (
          <div class="flex justify-center" ref={scrollToEnd}>
            <span class="inline-flex gap-3 text-6xl font-bold text-gray-400">
              <span class="animate-bounce [animation-delay:0ms]">.</span>
              <span class="animate-bounce [animation-delay:150ms]">.</span>
              <span class="animate-bounce [animation-delay:300ms]">.</span>
            </span>
          </div>
        )}
      </div>
      {state !== 'disconnected' && (
        <footer class="flex flex-col gap-4 border-t px-8 py-4">
          {isKeyboardConversation && (
            <div class="flex items-center gap-2">
              <AutosizeText
                class="ruz-input p-4 min-h-12 rounded-xl"
                containerClass="w-full"
                placeholder="Type your message..."
                maxLength={1024}
                focusSelf
                value={keyboardMessage}
                onInput={(e) => setKeyboardMessage(e.currentTarget.value)}
                onKeyDown={(e: KeyboardEvent) => {
                  if (!e.shiftKey && e.code === 'Enter') {
                    e.preventDefault();
                    sendKeyboardMessage();
                  }
                }}
              />
              <BtnPrimary onClick={sendKeyboardMessage}>
                <IcoSend />
              </BtnPrimary>
            </div>
          )}
          <div class="flex justify-center gap-4">
            {!lastMicrophoneError && (
              <BtnSecondary
                class={'px-4 rounded-full'}
                onClick={() => {
                  setIsKeyboardConversation((prev) => !prev);
                }}
              >
                <Case
                  when={isKeyboardConversation}
                  fallback={
                    <>
                      <IcoKeyboard class="size-5 mr-2" />
                      Switch to typing
                    </>
                  }
                >
                  <IcoMicrophone class="size-5 mr-2" />
                  Switch to voice input
                </Case>
              </BtnSecondary>
            )}
            <BtnWarning class={'px-4 rounded-full'} onClick={() => props.setShouldConnect(false)}>
              Pause conversation
            </BtnWarning>
          </div>
        </footer>
      )}
    </div>
  );
}

export function LiveKitConversation(props: {
  firstName: string;
  initialMessages: AiMessage[];
  onMessagesChange: (messages: AiMessage[]) => void;
  onData?: (data: LivekitConversationData) => void;
}) {
  const config = useConfiguration();
  const user = useCurrentUser();
  const [token, setToken] = useState<string | undefined>(undefined);
  const [shouldConnect, setShouldConnect] = useState(true);
  const nameToUse = props.firstName || user?.displayName || user?.name || '';

  // Set log level to warn to reduce verbosity
  useEffect(() => {
    setLogLevel('warn');
  }, []);

  useEffect(() => {
    async function fetchToken() {
      try {
        const callHistory = props.initialMessages
          .map((m) => (m.isAssistant ? `Assistant: ${m.content}` : `User: ${m.content}`))
          .join('\n');
        const result = await rpx.aiInterview.getLivekitToken({
          participantName: nameToUse,
          callHistory,
        });
        setToken(result.token);
      } catch (err) {
        showError(err);
      }
    }
    fetchToken();
  }, []);

  if (!token || !config.livekitUrl) {
    return <LoadingIndicator />;
  }

  return (
    <LiveKitRoom
      className="flex grow"
      serverUrl={config.livekitUrl}
      token={token}
      connect={shouldConnect}
      audio={true}
      video={false}
      onError={(error) => {
        console.error(error);
      }}
    >
      <ConversationUI
        firstName={props.firstName}
        initialMessages={props.initialMessages}
        setShouldConnect={setShouldConnect}
        onMessagesChange={(messages) => {
          // Do not update messages if we have disconnected
          if (shouldConnect) {
            props.onMessagesChange(messages);
          }
        }}
        onData={props.onData}
      />
    </LiveKitRoom>
  );
}
